import { Button, Circle, Divider, Flex, Image, Panel, Tag, Text, Tooltip } from 'components'
import { AuthContext, useWorkspace } from 'components/providers'
import { startCase } from 'lodash-es'
import { useContext } from 'react'
import { IntegrationTool, IntegrationType, IWorkspaceIntegration } from 'types'
import { useTheme } from 'utils'
import {
  connectionStatusRenderer,
  connectToIntegration,
  integrationLogoRenderer,
  integrationToolDescription,
  useActiveWorkspaceIntegration,
  useWorkspaceIntegrations,
} from '../../utils/integrationUtils'

interface Props {
  tool: IntegrationTool
  type: IntegrationType
  isDisabled?: boolean
}

export default function IntegrationCard({ tool, type, isDisabled }: Props) {
  const { theme } = useTheme()
  const { updateWorkspace, workspace } = useWorkspace()
  const { user } = useContext(AuthContext)
  const integration = useWorkspaceIntegrations(type)?.find(i => i.tool.name === tool)
  const connectionStatus = integration?.status || 'not-set'
  const statusDetails = connectionStatusRenderer(connectionStatus)
  const activeIntegration = useActiveWorkspaceIntegration(type)
  const description = integrationToolDescription(type)
  const notActive = activeIntegration && activeIntegration?.tool.name !== tool

  function handleConnect() {
    connectToIntegration(workspace?.id!, tool)
  }

  async function disconnectFromIntegration() {
    if (!integration) return
    if (!integration.accessToken && !integration.refreshToken) {
      return Promise.reject(new Error('No active session found for disconnection.'))
    }

    const updatedIntegrations =
      (workspace?.workspaceIntegrations?.map(i =>
        i.tool === integration.tool ? { ...i, status: 'disconnected' } : i
      ) as IWorkspaceIntegration[]) || []
    await updateWorkspace({ workspaceIntegrations: updatedIntegrations })
  }

  async function handleConfigureMapping() {
    if (!integration) return
    const updatedIntegrations =
      (workspace?.workspaceIntegrations?.map(i =>
        i.tool === integration.tool ? { ...i, status: 'needs-mapping' } : i
      ) as IWorkspaceIntegration[]) || []
    await updateWorkspace({ workspaceIntegrations: updatedIntegrations })
  }

  function optionRenderer(status: string) {
    switch (status) {
      case 'connected':
        return (
          <Flex direction="column" gap={4} flex={1} justify="space-between">
            <Flex direction="column" gap={2}>
              <Text textStyle="bodyMedium">Account: {integration?.account?.userEmail}</Text>
              <Text textStyle="bodyMedium">Organization: {integration?.account?.orgName}</Text>
            </Flex>

            <Flex gap={3}>
              <Button variant="outline" color="red.500" onClick={disconnectFromIntegration}>
                Disconnect
              </Button>
              <Button palette="primary" onClick={handleConfigureMapping}>
                Re-Configure Mapping
              </Button>
            </Flex>
          </Flex>
        )

      case 'needs-mapping':
        return (
          <Flex direction="column" gap={4} flex={1} justify="space-between">
            <Flex direction="column" gap={2}>
              <Text textStyle="bodyMedium">Account: {integration?.account?.userEmail}</Text>
              <Text textStyle="bodyMedium">Organization: {integration?.account?.orgName}</Text>
            </Flex>

            <Flex gap={3}>
              <Button variant="outline" color="red.500" onClick={disconnectFromIntegration}>
                Disconnect
              </Button>
              <Button palette="primary" onClick={handleConfigureMapping}>
                Configure Mapping
              </Button>
            </Flex>
          </Flex>
        )

      case 'not-set':
        return (
          <Flex direction="column" gap={4}>
            <Text textStyle="bodyMedium" fontWeight="normal">
              Connect your {tool} account to sync information between Momentify and {tool}.{' '}
              {description}
            </Text>
            <Tooltip
              content={`Only 1 ${startCase(
                type
              )} integration is allowed. Your team has already connected ${
                activeIntegration?.tool.name
              }.`}
              disabled={
                !notActive || (type != 'ATS' && type != 'CRM') || activeIntegration === undefined
              }
            >
              <Button
                palette="primary"
                onClick={handleConnect}
                alignSelf="flex-start"
                disabled={isDisabled || notActive}
              >
                Connect to {tool}
              </Button>
            </Tooltip>
          </Flex>
        )

      case 'disconnected':
        return (
          <Flex direction="column" gap={4}>
            <Text textStyle="bodyMedium" fontWeight="normal">
              Syncing leads between Momentify and {tool} is disconnected. Reconnect for seamless
              data flow.
            </Text>
            <Tooltip
              content={`Only 1 ${startCase(
                type
              )} integration is allowed. Your team has already connected ${
                activeIntegration?.tool.name
              }.`}
              disabled={
                !notActive || (type != 'ATS' && type != 'CRM') || activeIntegration === undefined
              }
            >
              <Button
                palette="primary"
                onClick={handleConnect}
                alignSelf="flex-start"
                disabled={isDisabled || notActive}
              >
                Connect to {tool}
              </Button>
            </Tooltip>
          </Flex>
        )

      case 'error':
        return (
          <Flex direction="column" gap={4}>
            <Text textStyle="bodyMedium" fontWeight="normal">
              Connection failed. Please check try again.
            </Text>
            <Button
              palette="primary"
              onClick={handleConnect}
              alignSelf="flex-start"
              disabled={isDisabled}
            >
              Re-try connection
            </Button>
          </Flex>
        )
    }
  }

  if (!workspace || !user) return

  return (
    <Panel
      p={5}
      gap={4}
      borderRadius="xl"
      boxShadow="0px 1px 4px 0px rgba(0, 0, 0, 0.06)"
      border="1px solid"
      borderColor={theme === 'light' ? 'rgba(23, 23, 23, 0.10)' : 'rgba(255, 255, 255, 0.10)'}
      opacity={isDisabled ? 0.5 : 1}
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Flex alignItems="center" gap={3}>
          <Image src={integrationLogoRenderer(tool)} width="45px" />
          <Flex direction="column" gap={0.5}>
            <Text textStyle="h5">{tool}</Text>
            <Tag w="100%">{type}</Tag>
          </Flex>
        </Flex>
        {connectionStatus !== 'not-set' && (
          <Flex alignItems="center" gap={2}>
            <Circle size="10px" bg={statusDetails?.color} />
            <Text textStyle="bodySmall" color={statusDetails?.color}>
              {statusDetails?.text}
            </Text>
          </Flex>
        )}
      </Flex>

      <Divider
        borderColor={theme === 'light' ? 'rgba(0, 0, 0, 0.10)' : 'rgba(255, 255, 255, 0.10)'}
      />

      {optionRenderer(connectionStatus)}
    </Panel>
  )
}
